import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'

import type { Props as TableProps } from '../Table'
import type { GridOptions } from 'ag-grid-enterprise'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement } from 'react'

import Table from '../Table'

import { getElementBySelectorAsync } from '~/utils/dom'

export type TableWrapperProps = TableProps & GridOptions

const TableWrapper = forwardRef<AgGridReact, TableWrapperProps>(
  ({ ...props }: TableWrapperProps, ref): ReactElement => {
    const tableRef = props.tableRef

    const [filtersHeight, setFiltersHeight] = useState(0)
    const [filtersRef, setFiltersRef] = useState<Element>()
    const [headerHeight, setHeaderHeight] = useState(0)
    const [headerRef, setHeaderRef] = useState<Element>()
    const [viewTitleHeight, setViewTitleHeight] = useState(0)
    const [viewTitleRef, setViewTitleRef] = useState<Element>()
    const [iapFiltersHeight, setIapFiltersHeight] = useState(0)
    const [iapFiltersRef, setIapFiltersRef] = useState<Element>()
    const [paginationHeight, setPaginationHeight] = useState(0)
    const [paginationRef, setPaginationRef] = useState<Element>()
    const [searchContainerHeight, setSearchContainerHeight] = useState(0)
    const [searchContainerRef, setSearchContainerRef] = useState<Element>()

    const layoutPaddingTopBottom = 30
    const searchContainerMarginBottom = 9
    const paginationMarginTop = 25

    const filtersObserver = useRef(
      new ResizeObserver((entries) => {
        setTimeout(() => setFiltersHeight(entries[0].borderBoxSize[0].blockSize), 0)
      })
    )

    const headerObserver = useRef(
      new ResizeObserver((entries) => {
        setTimeout(() => setHeaderHeight(entries[0].borderBoxSize[0].blockSize), 0)
      })
    )

    const viewTitleObserver = useRef(
      new ResizeObserver((entries) => {
        setTimeout(() => setViewTitleHeight(entries[0].borderBoxSize[0].blockSize), 0)
      })
    )

    const iapFiltersObserver = useRef(
      new ResizeObserver((entries) => {
        setTimeout(() => setIapFiltersHeight(entries[0].borderBoxSize[0].blockSize), 0)
      })
    )

    const paginationObserver = useRef(
      new ResizeObserver((entries) => {
        setTimeout(() => setPaginationHeight((entries[0].borderBoxSize[0]?.blockSize || 0) + paginationMarginTop), 0)
      })
    )

    const searchContainerObserver = useRef(
      new ResizeObserver((entries) => {
        setTimeout(
          () => setSearchContainerHeight(entries[0].borderBoxSize[0].blockSize + searchContainerMarginBottom),
          0
        )
      })
    )

    const setStyleAttribute = (element: HTMLElement | null, attrs: { [key: string]: string }) => {
      if (element) {
        for (const [key, value] of Object.entries(attrs)) {
          element.style.setProperty(key, value)
        }
      }
    }

    const computeStyle = useCallback(() => {
      if (tableRef?.current) {
        const tableBody: HTMLElement | null = tableRef.current.querySelector('.ag-body')

        setStyleAttribute(tableBody, {
          height: 'fit-content',
          'max-height': `calc(100vh - ${headerHeight}px - ${filtersHeight}px - ${viewTitleHeight}px - ${iapFiltersHeight}px - ${paginationHeight}px - ${layoutPaddingTopBottom}px - ${searchContainerHeight}px - 5.1rem)`,
          overflow: 'scroll',
        })
      }
    }, [
      headerHeight,
      filtersHeight,
      tableRef,
      viewTitleHeight,
      iapFiltersHeight,
      paginationHeight,
      searchContainerHeight,
    ])

    useEffect(() => {
      async function fetchHeaderRef() {
        const headerRef = await getElementBySelectorAsync('.header-component')
        setHeaderRef(headerRef)
      }

      fetchHeaderRef()
    }, [])

    useEffect(() => {
      async function fetchIapFiltersRef() {
        const iapFiltersRef = await getElementBySelectorAsync('.iap-filters')

        setIapFiltersRef(iapFiltersRef)
      }

      fetchIapFiltersRef()
    }, [])

    useEffect(() => {
      async function fetchViewTitleRef() {
        const viewTitleRef = await getElementBySelectorAsync('.layout-view-title')

        setViewTitleRef(viewTitleRef)
      }

      fetchViewTitleRef()
    }, [])

    useEffect(() => {
      async function fetchFiltersRef() {
        const filtersRef = await getElementBySelectorAsync('.filters-component')

        setFiltersRef(filtersRef)
      }

      fetchFiltersRef()
    }, [])

    useEffect(() => {
      async function fetchPaginationRef() {
        const paginationRef = await getElementBySelectorAsync('.table-pagination')

        setPaginationRef(paginationRef)
      }

      fetchPaginationRef()
    }, [])

    useEffect(() => {
      async function fetchSearchContainerRef() {
        const searchContainerRef = await getElementBySelectorAsync('.search-container')

        setSearchContainerRef(searchContainerRef)
      }

      fetchSearchContainerRef()
    }, [])

    useEffect(() => {
      const filtersCurrentObserver = filtersObserver.current
      const headerCurrentObserver = headerObserver.current
      const viewTitleCurrentObserver = viewTitleObserver.current
      const iapFiltersCurrentObserver = iapFiltersObserver.current
      const paginationCurrentObserver = paginationObserver.current
      const searchContainerCurrentObserver = searchContainerObserver.current

      filtersRef && filtersCurrentObserver.observe(filtersRef)
      headerRef && headerCurrentObserver.observe(headerRef)
      viewTitleRef && viewTitleCurrentObserver.observe(viewTitleRef)
      iapFiltersRef && iapFiltersCurrentObserver.observe(iapFiltersRef)
      paginationRef && paginationCurrentObserver.observe(paginationRef)
      searchContainerRef && searchContainerCurrentObserver.observe(searchContainerRef)

      return () => {
        filtersRef && filtersCurrentObserver.unobserve(filtersRef)
        headerRef && headerCurrentObserver.unobserve(headerRef)
        viewTitleRef && viewTitleCurrentObserver.observe(viewTitleRef)
        iapFiltersRef && iapFiltersCurrentObserver.observe(iapFiltersRef)
        paginationRef && paginationCurrentObserver.observe(paginationRef)
        searchContainerRef && searchContainerCurrentObserver.observe(searchContainerRef)
      }
    }, [filtersRef, headerRef, viewTitleRef, iapFiltersRef, paginationRef, searchContainerRef])

    useEffect(() => {
      computeStyle()
    }, [computeStyle])

    return <Table {...props} ref={ref} />
  }
)

TableWrapper.displayName = 'TableWrapper'

export default TableWrapper
